import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { billing } from '@/api/billing';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { Navigate } from 'react-router-dom';
import {
  Eye, EyeOff, CheckCircle2, XCircle, Loader2, Zap, ShieldCheck,
} from 'lucide-react';
import { cn } from '@/lib/cn';

interface FormValues {
  mode: 'test' | 'live';
  secretKey: string;
  webhookSecret: string;
  publicKey: string;
}

function ModeToggle({ value, onChange }: { value: 'test' | 'live'; onChange: (v: 'test' | 'live') => void }) {
  return (
    <div className="inline-flex rounded-md border border-border overflow-hidden text-sm">
      {(['test', 'live'] as const).map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          className={cn(
            'px-4 py-1.5 font-medium transition-colors capitalize',
            value === m
              ? m === 'live' ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white'
              : 'bg-transparent text-muted-foreground hover:bg-secondary/50',
          )}
        >
          {m}
        </button>
      ))}
    </div>
  );
}

function RevealInput({ label, hint, placeholder, registration, id }: {
  label: string;
  hint?: string | null;
  placeholder: string;
  id: string;
  registration: ReturnType<ReturnType<typeof useForm<FormValues>>['register']>;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {hint && (
        <p className="text-xs text-muted-foreground font-mono">
          Current: <span className="text-foreground/70">{hint}</span>
        </p>
      )}
      <div className="relative">
        <Input
          id={id}
          type={show ? 'text' : 'password'}
          placeholder={placeholder}
          className="pr-10 font-mono text-sm"
          {...registration}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={show ? 'Hide' : 'Show'}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

function StripeSettingsInner() {
  const qc = useQueryClient();

  const { data: current, isLoading } = useQuery({
    queryKey: ['stripe-config'],
    queryFn: () => billing.getStripeConfig(),
  });

  const { register, handleSubmit, watch, setValue, formState: { isDirty } } = useForm<FormValues>({
    defaultValues: { mode: 'test', secretKey: '', webhookSecret: '', publicKey: '' },
  });

  const mode = watch('mode');

  const saveMutation = useMutation({
    mutationFn: billing.putStripeConfig,
    onSuccess: () => {
      toast({ title: 'Stripe settings saved.' });
      void qc.invalidateQueries({ queryKey: ['stripe-config'] });
    },
    onError: (err) => {
      toast({ title: 'Save failed', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' });
    },
  });

  const [testResult, setTestResult] = useState<{ ok: boolean; label: string } | null>(null);
  const [testing, setTesting] = useState(false);

  async function runTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await billing.testStripeConfig();
      setTestResult({
        ok: r.ok,
        label: r.ok
          ? `Connected — ${r.displayName ?? r.accountId ?? 'account verified'}`
          : `Failed: ${r.message ?? r.error ?? 'unknown error'}`,
      });
    } catch (err) {
      setTestResult({ ok: false, label: err instanceof Error ? err.message : 'Connection failed' });
    } finally {
      setTesting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="Stripe"
        description="Configure platform-level Stripe credentials for reseller subscription billing."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={runTest}
              disabled={testing || !current?.secretKey_hint}
            >
              {testing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Zap className="h-4 w-4 mr-1.5" />}
              Test connection
            </Button>
          </div>
        }
      />

      <div className="max-w-2xl space-y-5">
        {/* Status card when configured */}
        {current?.secretKey_hint && (
          <Card className="border-emerald-200 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/30">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                    Stripe configured — {current.mode === 'live' ? 'Live mode' : 'Test mode'}
                  </p>
                  <p className="text-xs text-emerald-700/70 dark:text-emerald-400/70 font-mono mt-0.5">
                    {current.secretKey_hint} &bull; webhook {current.webhookSecret_set ? 'set' : 'not set'} &bull; {current.publicKey_hint}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Test result */}
        {testResult && (
          <div className={cn(
            'flex items-center gap-2.5 rounded-md border px-4 py-3 text-sm',
            testResult.ok
              ? 'border-emerald-200 bg-emerald-50/60 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300'
              : 'border-destructive/30 bg-destructive/5 text-destructive',
          )}>
            {testResult.ok
              ? <CheckCircle2 className="h-4 w-4 shrink-0" />
              : <XCircle className="h-4 w-4 shrink-0" />}
            {testResult.label}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Credentials</CardTitle>
            <CardDescription>
              Use test mode credentials during development. Switch to live when ready to charge.
              Keys are stored encrypted in the database — never in env files after initial setup.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={handleSubmit((data) => saveMutation.mutate(data))}
              className="space-y-5"
            >
              {/* Mode toggle */}
              <div className="space-y-1.5">
                <Label>Mode</Label>
                <div>
                  <ModeToggle value={mode} onChange={(v) => setValue('mode', v, { shouldDirty: true })} />
                </div>
                {mode === 'live' && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5">
                    Live mode will charge real payment methods. Double-check your keys.
                  </p>
                )}
              </div>

              <RevealInput
                id="secretKey"
                label="Secret Key"
                hint={current?.secretKey_hint}
                placeholder={mode === 'test' ? 'sk_test_…' : 'sk_live_…'}
                registration={register('secretKey')}
              />
              <RevealInput
                id="webhookSecret"
                label="Webhook Secret"
                hint={current?.webhookSecret_set ? '••••••••••••••••' : null}
                placeholder="whsec_…"
                registration={register('webhookSecret')}
              />
              <RevealInput
                id="publicKey"
                label="Publishable Key"
                hint={current?.publicKey_hint}
                placeholder={mode === 'test' ? 'pk_test_…' : 'pk_live_…'}
                registration={register('publicKey')}
              />

              <div className="pt-1 flex justify-end">
                <Button
                  type="submit"
                  disabled={saveMutation.isPending || !isDirty}
                  size="sm"
                >
                  {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                  Save credentials
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="border-dashed">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground/80">Webhook endpoint:</strong>{' '}
              <span className="font-mono">POST https://api.ivrello.com/api/stripe/webhook</span>
              <br />
              Add this URL in your Stripe dashboard → Developers → Webhooks. Required events:
              {' '}
              <span className="font-mono">
                customer.subscription.*, invoice.paid, invoice.payment_failed, invoice.upcoming
              </span>
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

export function StripeSettings() {
  const user = useCurrentUser();
  if (!user.is_super_admin) return <Navigate to="/dashboard" replace />;
  return <StripeSettingsInner />;
}
